import { Spinner } from '@cmsgov/design-system';
import {
  get,
  queryKeys,
  ResourceType,
  useContractRoleAssignmentsQuery,
  useCreateContractRoleAssignment,
  useCreateUser,
  useDeleteContractRoleAssignment,
} from '@access-cms/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldErrors } from '@/pages/application/types';
import { useContractData } from './context';
import RosterOwnersForm from './components/RosterOwnersForm';
import { RosterOwnerFormData } from './types';
import { useAlerts } from '@/hooks';

function normalizeEmail(email: string | undefined): string {
  return email?.trim().toLowerCase() ?? '';
}

function RosterOwnersTab() {
  const baseTestId = 'roster-owners-tab';

  const { contractId } = useContractData();
  const { errorAlert, successAlert } = useAlerts();
  const queryClient = useQueryClient();

  const createUser = useCreateUser();
  const createContractRoleAssignment = useCreateContractRoleAssignment(contractId || '');
  const deleteContractRoleAssignment = useDeleteContractRoleAssignment(contractId || '');
  const roleAssignmentsQuery = useContractRoleAssignmentsQuery(contractId || '', {
    enabled: !!contractId,
    filters: { active: true, roleType: 'ROSTER_OWNER' },
  });

  const primaryAssignment = roleAssignmentsQuery.data?.data?.find((i) => i.roleVariant == 'PRIMARY');
  const delegateAssignment = roleAssignmentsQuery.data?.data?.find((i) => i.roleVariant == 'ALTERNATE');

  const initialValues = useMemo(
    () => ({
      delegateOwnerEmail: delegateAssignment?.user?.email ?? '',
      delegateOwnerName: [delegateAssignment?.user?.firstName, delegateAssignment?.user?.lastName]
        .filter(Boolean)
        .join(' '),
      rosterOwnerEmail: primaryAssignment?.user?.email ?? '',
      rosterOwnerName: [primaryAssignment?.user?.firstName, primaryAssignment?.user?.lastName]
        .filter(Boolean)
        .join(' '),
    }),
    [delegateAssignment, primaryAssignment],
  );
  const [formData, setFormData] = useState<RosterOwnerFormData>(initialValues);
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const hasEmailChanges =
    normalizeEmail(formData.rosterOwnerEmail) !== normalizeEmail(initialValues.rosterOwnerEmail) ||
    normalizeEmail(formData.delegateOwnerEmail) !== normalizeEmail(initialValues.delegateOwnerEmail);

  useEffect(() => {
    if (!isSubmittingRef.current) {
      setFormData(initialValues);
    }
  }, [initialValues]);

  const fetchUserByEmail = (email: string) => {
    const normalizedEmail = normalizeEmail(email);

    return queryClient.fetchQuery({
      queryFn: ({ signal }) => get('/api/v1/users', { queryParams: { email: normalizedEmail }, signal }),
      queryKey: queryKeys[ResourceType.USERS].list({ email: normalizedEmail }),
    });
  };

  const createPlaceholderUser = async (email: string, fullName: string) => {
    const [firstName, ...rest] = fullName.trim().split(' ');

    return createUser.mutateAsync({
      email: normalizeEmail(email),
      firstName,
      lastName: rest.join(' '),
      userType: 'CONTRACTOR',
    });
  };

  const assignRosterOwner = async ({
    email,
    existingAssignmentId,
    fullName,
    roleVariant,
  }: {
    email: string;
    existingAssignmentId?: string;
    fullName: string;
    roleVariant: 'PRIMARY' | 'ALTERNATE';
  }) => {
    const result = await fetchUserByEmail(email);
    const existingUser = result.data?.[0];

    let userId: string;

    if (existingUser) {
      if (!existingUser.id) throw new Error('User lookup returned a user without an ID');
      userId = existingUser.id;
    } else {
      const newUser = await createPlaceholderUser(email, fullName);
      const newId = newUser.data?.id;
      if (!newId) throw new Error('Failed to create placeholder user: no ID returned');
      userId = newId;
    }

    if (existingAssignmentId) {
      await deleteContractRoleAssignment.mutateAsync({
        assignmentId: existingAssignmentId,
        contractId: contractId || '',
      });
    }

    await createContractRoleAssignment.mutateAsync({
      roleType: 'ROSTER_OWNER',
      roleVariant,
      userId,
    });
  };

  const handleSubmit = async () => {
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const rosterEmailChanged =
        normalizeEmail(formData.rosterOwnerEmail) !== normalizeEmail(initialValues.rosterOwnerEmail);
      const delegateEmailChanged =
        normalizeEmail(formData.delegateOwnerEmail) !== normalizeEmail(initialValues.delegateOwnerEmail);

      if (rosterEmailChanged) {
        await assignRosterOwner({
          email: normalizeEmail(formData.rosterOwnerEmail),
          existingAssignmentId: primaryAssignment?.id,
          fullName: formData.rosterOwnerName,
          roleVariant: 'PRIMARY',
        });
      }

      if (delegateEmailChanged) {
        if (formData.delegateOwnerEmail) {
          await assignRosterOwner({
            email: normalizeEmail(formData.delegateOwnerEmail),
            existingAssignmentId: delegateAssignment?.id,
            fullName: formData.delegateOwnerName ?? '',
            roleVariant: 'ALTERNATE',
          });
        } else if (delegateAssignment?.id) {
          await deleteContractRoleAssignment.mutateAsync({
            assignmentId: delegateAssignment.id,
            contractId: contractId || '',
          });
        }
      }

      successAlert({ dismissInSeconds: 30, title: 'Roster owners updated successfully' });
    } catch (error) {
      errorAlert({
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        title: 'Failed to update roster owners',
      });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (roleAssignmentsQuery.isLoading) {
    return (
      <div
        className='roster-owners-tab-content ds-u-justify-content--center '
        data-testid={baseTestId}>
        <div data-testid={`${baseTestId}-loading`}>
          <Spinner aria-valuetext='Loading roster owners...' />
        </div>
      </div>
    );
  }

  if (roleAssignmentsQuery.isError) {
    return (
      <div
        className='roster-owners-tab-content'
        data-testid={baseTestId}>
        <p
          className='ds-text'
          data-testid={`${baseTestId}-error`}>
          Failed to load roster owner data.
        </p>
      </div>
    );
  }

  return (
    <RosterOwnersForm
      data-testid={baseTestId}
      formData={formData}
      formErrors={formErrors}
      hasEmailChanges={hasEmailChanges}
      initialValues={initialValues}
      isSubmitting={isSubmitting}
      setFormData={setFormData}
      setFormErrors={setFormErrors}
      onSubmit={handleSubmit}
    />
  );
}

export default RosterOwnersTab;
